import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { RiDeleteBin6Line, RiAddCircleFill } from "react-icons/ri";
import { Images } from "../../../../../utils/imageConstants";
import { resolvePublicImageUrl } from "../../../../../helper/helper";
import { fetchDivisions } from "../../../../myTeam/api/myTeamApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEY } from "../../../../../services/queryEndPoint";
import { Api } from "../../../../../services/interceptor";
import { createMatch, scheduleMatchRequest, updateMatchOfficialsRequest, buildUtcMatchTimeFromDateAndTime, getScheduleMatchRequestErrorMessage, matchesBulk } from "../../../../myLeagues/api/myLeaguesApi";
import { findDivisionGroupForTeamIds, flattenDivisionTeams, type TeamOption } from "./TeamSelectModal";
import TickerAutomationModal from "./TickerAutomationModal";
import MaterialTimePicker from "../../../../myLeagues/components/MaterialTimePicker";
import { setTournamentMatches } from "../../../../../redux/slice/matchesUiSlice";
import { setActiveTab as setLeagueShellActiveTab } from "../../../../../redux/slice/activeTabSlice";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../../../redux/hooks";
import { selectSelectedLeague } from "../../../../../redux/slice/selectedLeagueSlice";
import { normalizePitchTypeForSchedule, normalizePlayoffTypeForSchedule, PITCH_TYPE_OPTIONS, PLAYOFF_TYPE_OPTIONS } from "./scheduleMatchEnums";
import { getTournamentOversForSchedule } from "./scheduleTournamentContext";
import type { ITournamentsByLeagueResponse } from "../../../types/home";
import FormDropdown from "../../../../../components/common/FormDropdown";
import { ProfileDatePicker } from "../../../../profileOnboarding/components/ProfileDatePicker";
import { extractGroundIdForSchedule, localDateAndTimeFromIso, resolveDivisionSelectionFromApi } from "./scheduleMatchPrefillUtils";
import MatchOfficialsFields from "./MatchOfficialsFields";
import { emptyOfficials, legacyOfficialFields, officialsFromMatch, toOfficialsRequest, type OfficialPick, type OfficialsForm } from "./matchOfficials";
import type { IMatchDetails } from "./matchesDetails/types/matchDetails.d";
import "./ScheduleMatchPage.scss";

type GroundOption = { groundId: string; groundName: string };

const parseOptIntLocal = (str: string): number | undefined => {
  const t = str.trim();
  if (!t) return undefined;
  const n = parseInt(t, 10);
  return Number.isFinite(n) ? n : undefined;
};

/** The app defaults the pitch to Rough and requires one. */
const DEFAULT_PITCH_TYPE = "Rough";
const DEFAULT_PLAYOFF_TYPE = "LEAGUE_MATCH";

/** A stored stage ("SEMI_FINAL_1") back to the value the backend accepts ("SEMI_FINAL"); anything unknown / empty (NONE) becomes League Match. */
const playoffValueForForm = (stored?: string | null): string => {
  const base = normalizePlayoffTypeForSchedule((stored ?? "").replace(/_\d+$/, ""));
  return PLAYOFF_TYPE_OPTIONS.some((o) => o.value === base) ? base : DEFAULT_PLAYOFF_TYPE;
};

export type ScheduleTab = "single" | "bulk";

/** Single match: team picks then details + POST match → POST scheduleMatch */
export type SingleFlowPhase = "teams" | "details";

interface BulkRow {
  localId: string;
  team1Id: string;
  team2Id: string;
  groundId: string;
  matchDate: string;
  city: string;
  startTime: string;
  pitchType: string;
}

function teamLogoSrc(team: TeamOption | null | undefined): string {
  const url = resolvePublicImageUrl(team?.imageUrl ?? null);
  return url || Images.team_logo;
}

/** Tournament-available umpires / scorers: [{ id, name, isUmpire | isScorer }]. isPerson false = a team entry. */
function unwrapPersonArray(data: unknown): OfficialPick[] {
  const raw = Array.isArray(data) ? data : data && typeof data === "object" && "data" in data && Array.isArray((data as { data: unknown }).data) ? (data as { data: unknown[] }).data : [];
  return (raw as Record<string, unknown>[])
    .map((u) => ({
      id: String(u.id ?? u.umpireId ?? u.scorerId ?? u.playerId ?? ""),
      name: String(u.name ?? u.umpireName ?? u.scorerName ?? u.playerFirstName ?? ""),
      isPerson: Boolean(u.isUmpire ?? u.isScorer ?? true),
    }))
    .filter((p) => p.id);
}

function makeBulkRow(): BulkRow {
  return {
    localId: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    team1Id: "",
    team2Id: "",
    groundId: "",
    matchDate: "",
    city: "",
    startTime: "",
    pitchType: DEFAULT_PITCH_TYPE,
  };
}

export default function ScheduleMatchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const leagueId = selectedLeague?.leagueId ?? null;
  const tournamentId = sessionStorage.getItem("tournamentId");
  const tournamentName = sessionStorage.getItem("tournamentName") ?? "Schedule Match";
  const matchType = sessionStorage.getItem("tournamentMatchType") ?? "";
  const tournamentCategory = sessionStorage.getItem("tournamentCategory") ?? "";

  const editMatchId = searchParams.get("matchId")?.trim() || null;
  const isEditMode = Boolean(editMatchId);
  const divisionLeafFallbackRef = useRef("");
  const prefillAppliedRef = useRef(false);

  const [activeTab, setActiveTab] = useState<ScheduleTab>("single");
  const [team1, setTeam1] = useState<TeamOption | null>(null);
  const [team2, setTeam2] = useState<TeamOption | null>(null);
  const [tickerModalOpen, setTickerModalOpen] = useState(false);
  const [singleFlowPhase, setSingleFlowPhase] = useState<SingleFlowPhase>("teams");
  const [draftMatchId, setDraftMatchId] = useState<string | null>(null);
  const [committedTeam1SnapId, setCommittedTeam1SnapId] = useState<string | null>(null);
  const [committedTeam2SnapId, setCommittedTeam2SnapId] = useState<string | null>(null);
  const [pitchType, setPitchType] = useState<string>(DEFAULT_PITCH_TYPE);
  const [playoffType, setPlayoffType] = useState<string>(DEFAULT_PLAYOFF_TYPE);
  const [officials, setOfficials] = useState<OfficialsForm>(emptyOfficials);
  const [matchOversInput, setMatchOversInput] = useState("");
  const [maxBowlerInput, setMaxBowlerInput] = useState("");
  const [city, setCity] = useState("");
  const [matchDate, setMatchDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [groundId, setGroundId] = useState("");
  const [resolvedDivisionId, setResolvedDivisionId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [editPrefilled, setEditPrefilled] = useState(false);

  const [bulkRows, setBulkRows] = useState<BulkRow[]>(() => [makeBulkRow()]);
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  useEffect(() => {
    dispatch(setTournamentMatches(true));
    return () => {
      dispatch(setTournamentMatches(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (activeTab !== "single" && !isEditMode) {
      setSingleFlowPhase("teams");
      setTeam1(null);
      setTeam2(null);
      setDraftMatchId(null);
      setCommittedTeam1SnapId(null);
      setCommittedTeam2SnapId(null);
      setResolvedDivisionId("");
    }
  }, [activeTab, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setEditPrefilled(true);
    } else {
      setEditPrefilled(false);
    }
    prefillAppliedRef.current = false;
    divisionLeafFallbackRef.current = "";
  }, [isEditMode, editMatchId]);

  const { data: tournamentsByLeague } = useQuery({
    queryKey: [QUERY_KEY.getTournamentsByLeagues, leagueId],
    queryFn: async () => {
      if (!leagueId) return null;
      const res = await Api.get<ITournamentsByLeagueResponse>(`${QUERY_KEY.getTournamentsByLeagues}/${leagueId}`);
      return res?.data ?? null;
    },
    enabled: !!leagueId,
  });

  const scheduleTournamentRow = useMemo(() => {
    if (!tournamentId?.trim() || !tournamentsByLeague?.tournamentResponses?.length) return null;
    return tournamentsByLeague.tournamentResponses.find((t) => t.tournamentId === tournamentId) ?? null;
  }, [tournamentId, tournamentsByLeague]);

  const { matchOvers: tournamentMatchOversStr, maxOversPerBowler: tournamentMaxBowlerStr } = getTournamentOversForSchedule(scheduleTournamentRow);

  const { data: divisionsPayload } = useQuery({
    queryKey: [QUERY_KEY.getDivisions, tournamentId],
    queryFn: () => (tournamentId ? fetchDivisions(tournamentId) : Promise.resolve({ divisionResponseList: [] })),
    enabled: !!tournamentId,
  });

  const divisions = divisionsPayload?.divisionResponseList ?? [];

  /** All teams in the tournament (across every division and group). */
  const allTeams = useMemo(() => flattenDivisionTeams(divisions), [divisions]);

  const { data: groundsRaw, isFetched: groundsFetched } = useQuery({
    queryKey: [QUERY_KEY.groundsByLeagueId, leagueId],
    queryFn: async () => {
      if (!leagueId) return [];
      const res = await Api.get<GroundOption[]>(`${QUERY_KEY.groundsByLeagueId}/${leagueId}`);
      return Array.isArray(res?.data) ? res.data : [];
    },
    enabled: !!leagueId,
  });
  const grounds: GroundOption[] = (groundsRaw ?? []) as GroundOption[];
  const groundsReady = !leagueId || groundsFetched;

  const {
    data: editMatchDetails,
    isLoading: editMatchLoading,
    isError: editMatchError,
  } = useQuery({
    queryKey: [QUERY_KEY.getMatchInfo, editMatchId, "schedule-edit"],
    queryFn: async () => {
      const res = await Api.get<IMatchDetails>(`${QUERY_KEY.getMatchInfo}/${editMatchId}`);
      return res.data;
    },
    enabled: !!editMatchId,
  });

  // The tournament's available umpires / scorers (same lists the mobile app offers).
  const { data: umpiresRaw } = useQuery({
    queryKey: [QUERY_KEY.getAvailableUmpires, tournamentId],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getAvailableUmpires}/${tournamentId}`))?.data,
    enabled: !!tournamentId,
  });
  const umpires = useMemo(() => unwrapPersonArray(umpiresRaw), [umpiresRaw]);

  const { data: scorersRaw } = useQuery({
    queryKey: [QUERY_KEY.getAvailableScorers, tournamentId],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getAvailableScorers}/${tournamentId}`))?.data,
    enabled: !!tournamentId,
  });
  const scorers = useMemo(() => unwrapPersonArray(scorersRaw), [scorersRaw]);

  const pitchTypeDropdownOptions = useMemo(() => PITCH_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })), []);

  const playoffDropdownOptions = useMemo(() => [{ value: "", label: "Select playoff type" }, ...PLAYOFF_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))], []);

  const groundDropdownOptions = useMemo(() => [{ value: "", label: "Select ground" }, ...grounds.map((g) => ({ value: g.groundId, label: g.groundName }))], [grounds]);

  const team1DropdownOptions = useMemo(() => [{ value: "", label: "Select Team 1 Name" }, ...allTeams.map((t) => ({ value: t.teamId, label: t.teamName }))], [allTeams]);

  const team2DropdownOptions = useMemo(() => [{ value: "", label: "Select Team 2 Name" }, ...allTeams.filter((t) => t.teamId !== team1?.teamId).map((t) => ({ value: t.teamId, label: t.teamName }))], [allTeams, team1?.teamId]);

  const exitSchedulePage = () => {
    dispatch(setTournamentMatches(false));
    dispatch(setLeagueShellActiveTab("tournaments"));
    navigate(-1);
  };

  useEffect(() => {
    if (!editMatchId || !editMatchError) return;
    toast.error("Could not load match.");
    dispatch(setTournamentMatches(false));
    dispatch(setLeagueShellActiveTab("tournaments"));
    navigate(-1);
  }, [editMatchId, editMatchError, dispatch, navigate]);

  useEffect(() => {
    if (!isEditMode || !editMatchId || !editMatchDetails || prefillAppliedRef.current) return;
    if (!groundsReady) return;

    const st = (editMatchDetails.matchStatus ?? "").toUpperCase().replace(/\s+/g, "_");
    if (st && st !== "SCHEDULED" && st !== "UPCOMING" && st !== "NOT_STARTED") {
      prefillAppliedRef.current = true;
      toast.error("Only scheduled matches can be edited here.");
      dispatch(setTournamentMatches(false));
      dispatch(setLeagueShellActiveTab("tournaments"));
      navigate(-1);
      return;
    }

    const divs = divisionsPayload?.divisionResponseList ?? [];
    const m = editMatchDetails;
    const rawDiv = (m.divisionId ?? "").toString().trim();
    divisionLeafFallbackRef.current = "";

    let resolved = rawDiv ? resolveDivisionSelectionFromApi(rawDiv, divs) : null;
    if (!resolved) {
      resolved = findDivisionGroupForTeamIds(divs, m.teamOne?.teamId ?? "", m.teamTwo?.teamId ?? "");
    }
    if (resolved) {
      setResolvedDivisionId(resolved.divisionId);
    } else if (rawDiv) {
      divisionLeafFallbackRef.current = rawDiv;
      setResolvedDivisionId(rawDiv);
    }

    setTeam1({
      teamId: m.teamOne.teamId,
      teamName: m.teamOne.teamName,
      teamSnapshotId: m.teamOne.teamSnapshotId ?? "",
      imageUrl: null,
    });
    setTeam2({
      teamId: m.teamTwo.teamId,
      teamName: m.teamTwo.teamName,
      teamSnapshotId: m.teamTwo.teamSnapshotId ?? "",
      imageUrl: null,
    });

    setDraftMatchId(editMatchId);
    setCommittedTeam1SnapId(m.teamOne.teamSnapshotId ?? null);
    setCommittedTeam2SnapId(m.teamTwo.teamSnapshotId ?? null);
    setSingleFlowPhase("details");
    setActiveTab("single");

    setCity((m.city ?? "").trim());
    const { date, time } = localDateAndTimeFromIso(m.startDate);
    setMatchDate(date);
    setStartTime(time);

    setPitchType(m.pitchType ? normalizePitchTypeForSchedule(m.pitchType) : DEFAULT_PITCH_TYPE);
    setPlayoffType(playoffValueForForm(m.playoffType));

    setOfficials(officialsFromMatch(m));
    setMatchOversInput(String(m.matchOvers ?? (tournamentMatchOversStr || "")));
    setMaxBowlerInput(String(m.maxOversPerBowler ?? (tournamentMaxBowlerStr || "")));

    setGroundId(extractGroundIdForSchedule(m.groundId, grounds));

    prefillAppliedRef.current = true;
    setEditPrefilled(true);
  }, [isEditMode, editMatchId, editMatchDetails, divisionsPayload, grounds, groundsReady, dispatch, navigate]);

  const handleHeaderBack = () => {
    if (isEditMode) {
      exitSchedulePage();
      return;
    }
    if (activeTab !== "single") {
      exitSchedulePage();
      return;
    }
    if (singleFlowPhase === "details") {
      setSingleFlowPhase("teams");
      setDraftMatchId(null);
      return;
    }
    exitSchedulePage();
  };

  const handleTeam1Select = (teamId: string) => {
    const t = allTeams.find((x) => x.teamId === teamId) ?? null;
    setTeam1(t);
    if (t && team2?.teamId === t.teamId) setTeam2(null);
  };

  const handleTeam2Select = (teamId: string) => {
    const t = allTeams.find((x) => x.teamId === teamId) ?? null;
    setTeam2(t);
  };

  const canCreateMatch = Boolean(team1 && team2 && team1.teamId !== team2.teamId);

  const handleSingleFinalNext = async () => {
    if (!canCreateMatch || !team1 || !team2) {
      toast.error("Select two different teams.");
      return;
    }
    if (!team1.teamSnapshotId?.trim() || !team2.teamSnapshotId?.trim()) {
      toast.error("Missing team snapshot id for one or both teams.");
      return;
    }
    if (!groundId.trim()) {
      toast.error("Ground is required.");
      return;
    }
    if (!city.trim()) {
      toast.error("City is required.");
      return;
    }
    if (!matchDate) {
      toast.error("Match date is required.");
      return;
    }
    if (!startTime) {
      toast.error("Start time is required.");
      return;
    }

    const inferred = findDivisionGroupForTeamIds(divisions, team1.teamId, team2.teamId);
    if (!inferred && !resolvedDivisionId) {
      toast.error("Selected teams are not in the same division or group.");
      return;
    }
    // Parent division (not the group) — the match is stored against it.
    const leafDivision =
      (inferred?.divisionId ?? "") ||
      resolvedDivisionId ||
      divisionLeafFallbackRef.current.trim();
    if (!leafDivision) {
      toast.error("Could not resolve division for selected teams.");
      return;
    }
    if (inferred && inferred.divisionId !== resolvedDivisionId) {
      setResolvedDivisionId(inferred.divisionId);
    }
    const startDate = buildUtcMatchTimeFromDateAndTime(matchDate, startTime);
    if (!startDate) {
      toast.error("Invalid date or time.");
      return;
    }
    if (!pitchType.trim()) {
      toast.error("Please select pitch type.");
      return;
    }
    // Edit only (as in the app): overs and the per-bowler limit are per-match and required.
    let matchOversValue = parseOptIntLocal(tournamentMatchOversStr);
    let maxBowlerValue = parseOptIntLocal(tournamentMaxBowlerStr);
    if (isEditMode) {
      const cap = parseOptIntLocal(tournamentMatchOversStr) ?? 450;
      const overs = matchOversInput.trim() === "" ? undefined : Number(matchOversInput);
      if (overs === undefined) { toast.error("Match overs is required."); return; }
      if (!Number.isInteger(overs) || overs < 1) { toast.error("Match overs should be greater than 0."); return; }
      if (overs > cap) { toast.error(`Match overs should not be greater than ${cap}.`); return; }
      const bowler = maxBowlerInput.trim() === "" ? undefined : Number(maxBowlerInput);
      if (bowler === undefined) { toast.error("Max overs per bowler is required."); return; }
      if (!Number.isInteger(bowler) || bowler < 1) { toast.error("Max overs per bowler should be greater than 0."); return; }
      matchOversValue = overs;
      maxBowlerValue = bowler;
    }
    const officialsRequest = toOfficialsRequest(officials);

    setIsSaving(true);
    try {
      // Create the match if it doesn't exist yet (combined single-step flow).
      let matchId = draftMatchId;
      if (!matchId) {
        matchId = await createMatch({
          teamOneSnapshotId: team1.teamSnapshotId,
          teamTwoSnapshotId: team2.teamSnapshotId,
          tournamentId: tournamentId?.trim() ? tournamentId : null,
          leagueId: leagueId?.trim() ? leagueId : null,
        });
        setDraftMatchId(matchId);
        setCommittedTeam1SnapId(team1.teamSnapshotId);
        setCommittedTeam2SnapId(team2.teamSnapshotId);
      }

      // Only send team snap ids when they changed from what was committed.
      const t1Changed =
        committedTeam1SnapId != null && team1.teamSnapshotId !== committedTeam1SnapId;
      const t2Changed =
        committedTeam2SnapId != null && team2.teamSnapshotId !== committedTeam2SnapId;

      await scheduleMatchRequest({
        matchId,
        startDate,
        city: city.trim(),
        divisionId: leafDivision,
        pitchType: pitchType || undefined,
        playoffType: playoffType || undefined,
        groundId: groundId.trim() || undefined,
        matchType: matchType || undefined,
        tournamentCategory: tournamentCategory || undefined,
        matchOvers: matchOversValue,
        maxOversPerBowler: maxBowlerValue,
        ...legacyOfficialFields(officials),
        officials: officialsRequest,
        teamOneSnapShotId: t1Changed ? team1.teamSnapshotId : undefined,
        teamTwoSnapShotId: t2Changed ? team2.teamSnapshotId : undefined,
      });
      // scheduleMatch ignores an empty officials list, so clearing the last official goes through the officials endpoint.
      if (isEditMode && officialsRequest.length === 0 && (editMatchDetails?.officials?.length ?? 0) > 0) {
        await updateMatchOfficialsRequest(matchId, []);
      }
      if (t1Changed) setCommittedTeam1SnapId(team1.teamSnapshotId);
      if (t2Changed) setCommittedTeam2SnapId(team2.teamSnapshotId);
      toast.success(isEditMode ? "Match updated." : "Match scheduled.");
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.matchByTournamentIdAndStatusPageable] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.matchByTeamAndStatus] });
      exitSchedulePage();
    } catch (err) {
      console.error("scheduleMatchRequest failed:", err);
      toast.error(getScheduleMatchRequestErrorMessage(err, isEditMode ? "Failed to update match." : "Failed to schedule match."));
    } finally {
      setIsSaving(false);
    }
  };

  // Bulk schedule helpers
  const updateBulkRow = (localId: string, patch: Partial<BulkRow>) => {
    setBulkRows((prev) => prev.map((r) => (r.localId === localId ? { ...r, ...patch } : r)));
  };

  const removeBulkRow = (localId: string) => {
    setBulkRows((prev) => {
      const next = prev.filter((r) => r.localId !== localId);
      return next.length === 0 ? [makeBulkRow()] : next;
    });
  };

  const addBulkRow = () => {
    setBulkRows((prev) => [...prev, makeBulkRow()]);
  };

  const handleBulkSchedule = async () => {
    // Every column is required. A row that is completely blank is ignored; any other row must be complete,
    // and the first problem is named instead of the row being dropped silently.
    const isBlankRow = (r: BulkRow) => ![r.team1Id, r.team2Id, r.groundId, r.matchDate, r.city, r.startTime].some((v) => v.trim());
    const filledRows = bulkRows.filter((r) => !isBlankRow(r));
    for (const r of filledRows) {
      const n = bulkRows.indexOf(r) + 1;
      const missing =
        !r.team1Id.trim() ? "Team One"
        : !r.team2Id.trim() ? "Team Two"
        : !r.groundId.trim() ? "Ground"
        : !r.matchDate.trim() ? "Date"
        : !r.city.trim() ? "City"
        : !r.startTime.trim() ? "Start Time"
        : !r.pitchType.trim() ? "Pitch Type"
        : "";
      if (missing) {
        toast.error(`Row ${n}: ${missing} is required.`);
        return;
      }
      if (r.team1Id === r.team2Id) {
        toast.error(`Row ${n}: choose two different teams.`);
        return;
      }
    }
    const validRows = filledRows;
    if (validRows.length === 0) {
      toast.error("Add at least one match row.");
      return;
    }
    // Resolve parent divisionId from first row's teams. Same-division/group validation
    // across rows is intentionally not enforced.
    const firstInferred = findDivisionGroupForTeamIds(divisions, validRows[0].team1Id, validRows[0].team2Id);
    const divisionForBulk = firstInferred?.divisionId ?? "";

    const matchRequestList = validRows.map((r) => {
      const t1 = allTeams.find((t) => t.teamId === r.team1Id);
      const t2 = allTeams.find((t) => t.teamId === r.team2Id);
      const g = grounds.find((x) => x.groundId === r.groundId);
      const utc = buildUtcMatchTimeFromDateAndTime(r.matchDate, r.startTime);
      return {
        teamOneName: t1?.teamName ?? "",
        teamTwoName: t2?.teamName ?? "",
        groundName: g?.groundName ?? "",
        matchDate: r.matchDate,
        startTime: r.startTime,
        city: r.city.trim(),
        pitchType: r.pitchType || undefined,
        utcMatchTime: utc,
      };
    });

    setIsBulkSaving(true);
    try {
      await matchesBulk({
        divisionId: divisionForBulk,
        matchRequestList,
      });
      toast.success(`Scheduled ${matchRequestList.length} match(es).`);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.matchByTournamentIdAndStatusPageable] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.matchByTeamAndStatus] });
      exitSchedulePage();
    } catch (err) {
      console.error("matchesBulk failed:", err);
      toast.error("Failed to schedule matches.");
    } finally {
      setIsBulkSaving(false);
    }
  };

  const TABS: { key: ScheduleTab; label: string }[] = [
    { key: "single", label: "Single Match" },
    { key: "bulk", label: "Bulk Schedule" },
  ];

  const showEditSpinner = isEditMode && !editMatchError && (editMatchLoading || !editMatchDetails || !editPrefilled);

  return (
    <div className="create-tournament-wrapper schedule-match-page">
      <div className="background-image-wrapper">
        <div className="background-image">
          <p>{tournamentName}</p>
        </div>
      </div>
      <div className="create-tourmament-wrap schedule-match-wrap">
        <div className="ct-header">
          <img src={Images.previous_icon} alt="Back" onClick={handleHeaderBack} style={{ cursor: "pointer" }} />
          <h1 className="heading">{isEditMode ? "Edit Match" : "Schedule Match"}</h1>
        </div>

        <div className="schedule-match-content">
          {showEditSpinner ? (
            <p className="schedule-match-placeholder" role="status">
              Loading match…
            </p>
          ) : (
            <>
              {!isEditMode && (
                <div className="schedule-match-tabs">
                  {TABS.map(({ key, label }) => (
                    <button key={key} type="button" className={`schedule-match-tab ${activeTab === key ? "active" : ""}`} onClick={() => setActiveTab(key)}>
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {activeTab === "single" && (
                <div className="schedule-match-single">
                  <div className="schedule-match-team-row">
                    <div className="schedule-match-team-col">
                      <label className="schedule-match-team-label">Team 1</label>
                      <div className={`schedule-match-team-select${team1 ? " is-filled" : ""}`}>
                        {team1 && (
                          <span className="schedule-match-team-select-avatar" aria-hidden>
                            <img src={teamLogoSrc(team1)} alt="" />
                          </span>
                        )}
                        <FormDropdown
                          id="schedule-page-team-1"
                          value={team1?.teamId ?? ""}
                          onChange={handleTeam1Select}
                          options={team1DropdownOptions}
                          placeholder="Select Team 1 Name"
                          disabled={allTeams.length === 0}
                          className="schedule-match-team-dropdown"
                          portal
                        />
                      </div>
                    </div>

                    <div className="schedule-match-vs-pill" aria-hidden>
                      VS
                    </div>

                    <div className="schedule-match-team-col">
                      <label className="schedule-match-team-label">Team 2</label>
                      <div className={`schedule-match-team-select${team2 ? " is-filled" : ""}`}>
                        {team2 && (
                          <span className="schedule-match-team-select-avatar" aria-hidden>
                            <img src={teamLogoSrc(team2)} alt="" />
                          </span>
                        )}
                        <FormDropdown
                          id="schedule-page-team-2"
                          value={team2?.teamId ?? ""}
                          onChange={handleTeam2Select}
                          options={team2DropdownOptions}
                          placeholder="Select Team 2 Name"
                          disabled={!team1 || allTeams.length === 0}
                          className="schedule-match-team-dropdown"
                          portal
                        />
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const teamsReady = canCreateMatch;
                    return (
                      <>
                      <div
                        className={`schedule-match-details-panel${teamsReady ? "" : " is-disabled"}`}
                        aria-disabled={!teamsReady}
                      >
                        <div className="schedule-match-details-grid">
                          <div className="schedule-match-field">
                            <label>
                              Pitch Type <span className="req">*</span>
                            </label>
                            <FormDropdown id="schedule-page-pitch-type" value={pitchType} onChange={setPitchType} options={pitchTypeDropdownOptions} placeholder="Select pitch type" />
                          </div>
                          <div className="schedule-match-field">
                            <label>Playoff Type</label>
                            <FormDropdown id="schedule-page-playoff-type" value={playoffType} onChange={setPlayoffType} options={playoffDropdownOptions} placeholder="Select playoff type" />
                          </div>

                          <div className="schedule-match-field">
                            <label>
                              Ground <span className="req">*</span>
                            </label>
                            <FormDropdown id="schedule-page-ground" value={groundId} onChange={setGroundId} options={groundDropdownOptions} placeholder="Select ground" />
                          </div>
                          <div className="schedule-match-field">
                            <label>
                              City <span className="req">*</span>
                            </label>
                            <input type="text" placeholder="City Name" value={city} onChange={(e) => setCity(e.target.value)} />
                          </div>
                          <div className="schedule-match-field schedule-match-field--date">
                            <label htmlFor="schedule-page-match-date">
                              Match Date <span className="req">*</span>
                            </label>
                            <ProfileDatePicker id="schedule-page-match-date" value={matchDate} onChange={setMatchDate} placeholder="Select date" disablePast={!isEditMode} />
                          </div>

                          <div className="schedule-match-field">
                            <label>
                              Start Time <span className="req">*</span>
                            </label>
                            <MaterialTimePicker value={startTime} onChange={setStartTime} placeholder="Select time" />
                          </div>
                          {isEditMode && (
                            <>
                              <div className="schedule-match-field">
                                <label htmlFor="schedule-page-match-overs">
                                  Match Overs <span className="req">*</span>
                                </label>
                                <input id="schedule-page-match-overs" type="text" inputMode="numeric" placeholder="Overs" value={matchOversInput} onChange={(e) => setMatchOversInput(e.target.value.replace(/\D/g, ""))} />
                              </div>
                              <div className="schedule-match-field">
                                <label htmlFor="schedule-page-max-bowler">
                                  Max Overs Per Bowler <span className="req">*</span>
                                </label>
                                <input id="schedule-page-max-bowler" type="text" inputMode="numeric" placeholder="Overs per bowler" value={maxBowlerInput} onChange={(e) => setMaxBowlerInput(e.target.value.replace(/\D/g, ""))} />
                              </div>
                            </>
                          )}
                        </div>

                        <MatchOfficialsFields value={officials} onChange={setOfficials} umpires={umpires} scorers={scorers} />

                        <div className="schedule-match-ticker">
                          <span>Ticker Automation</span>
                          <button type="button" className="schedule-match-setup-link" onClick={() => setTickerModalOpen(true)} disabled={!draftMatchId} title={!draftMatchId ? "Match must be created first" : undefined}>
                            Set-Up
                          </button>
                        </div>
                      </div>

                      <div className="schedule-match-actions">
                        <button
                          type="button"
                          className="schedule-match-btn schedule-match-btn--next"
                          onClick={() => void handleSingleFinalNext()}
                          disabled={!teamsReady || isSaving}
                        >
                          {isSaving ? "Saving…" : isEditMode ? "Save" : "Save"}
                        </button>
                      </div>
                    </>
                    );
                  })()}
                </div>
              )}

              {activeTab === "bulk" && (
                <div className="schedule-match-bulk">
                  <div className="schedule-bulk-table-wrap">
                    <div className="schedule-bulk-table-header">
                      <span>Schedule Matches in Bulk</span>
                    </div>
                    <div className="schedule-bulk-table-scroll">
                      <table className="schedule-bulk-table">
                        <thead>
                          <tr>
                            <th className="schedule-bulk-col--action" />
                            <th className="schedule-bulk-col--num">#</th>
                            <th>Team One*</th>
                            <th>Team Two*</th>
                            <th>Ground*</th>
                            <th>Date(YYYY-MM-DD)*</th>
                            <th>City*</th>
                            <th>Start Time*</th>
                            <th>Pitch Type*</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkRows.map((row, idx) => {
                            const team2OptsForRow = [{ value: "", label: "Team two..." }, ...allTeams.filter((t) => t.teamId !== row.team1Id).map((t) => ({ value: t.teamId, label: t.teamName }))];
                            return (
                              <tr key={row.localId}>
                                <td className="schedule-bulk-col--action">
                                  <button type="button" className="schedule-bulk-row-delete" onClick={() => removeBulkRow(row.localId)} title="Remove row">
                                    <RiDeleteBin6Line />
                                  </button>
                                </td>
                                <td className="schedule-bulk-col--num">{idx + 1}</td>
                                <td>
                                  <FormDropdown
                                    value={row.team1Id}
                                    onChange={(v) =>
                                      updateBulkRow(row.localId, {
                                        team1Id: v,
                                        team2Id: row.team2Id === v ? "" : row.team2Id,
                                      })
                                    }
                                    options={[
                                      { value: "", label: "Team one..." },
                                      ...allTeams.map((t) => ({
                                        value: t.teamId,
                                        label: t.teamName,
                                      })),
                                    ]}
                                    placeholder="Team one..."
                                    portal
                                    searchable
                                    searchPlaceholder="Search team..."
                                  />
                                </td>
                                <td>
                                  <FormDropdown value={row.team2Id} onChange={(v) => updateBulkRow(row.localId, { team2Id: v })} options={team2OptsForRow} placeholder="Team two..." disabled={!row.team1Id} portal searchable searchPlaceholder="Search team..." />
                                </td>
                                <td>
                                  <FormDropdown
                                    value={row.groundId}
                                    onChange={(v) => updateBulkRow(row.localId, { groundId: v })}
                                    options={[
                                      { value: "", label: "Ground..." },
                                      ...grounds.map((g) => ({
                                        value: g.groundId,
                                        label: g.groundName,
                                      })),
                                    ]}
                                    placeholder="Ground..."
                                    portal
                                    searchable
                                    searchPlaceholder="Search ground..."
                                  />
                                </td>
                                <td className="schedule-bulk-col--date">
                                  <ProfileDatePicker id={`schedule-bulk-date-${row.localId}`} value={row.matchDate} onChange={(v) => updateBulkRow(row.localId, { matchDate: v })} placeholder="YYYY-MM-DD" disablePast portal />
                                </td>
                                <td>
                                  <input type="text" value={row.city} onChange={(e) => updateBulkRow(row.localId, { city: e.target.value })} placeholder="City..." />
                                </td>
                                <td className="schedule-bulk-col--time">
                                  <MaterialTimePicker value={row.startTime} onChange={(v) => updateBulkRow(row.localId, { startTime: v })} placeholder="Start Time..." />
                                </td>
                                <td>
                                  <FormDropdown
                                    value={row.pitchType}
                                    onChange={(v) => updateBulkRow(row.localId, { pitchType: v })}
                                    options={PITCH_TYPE_OPTIONS.map((o) => ({
                                      value: o.value,
                                      label: o.label,
                                    }))}
                                    placeholder="Pitch Type..."
                                    portal
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="schedule-bulk-table-footer">
                      <button type="button" className="schedule-bulk-add-row" onClick={addBulkRow}>
                        <RiAddCircleFill aria-hidden />
                        <span>Add another row</span>
                      </button>
                    </div>
                  </div>

                  <div className="schedule-match-actions">
                    <button type="button" className="schedule-match-btn schedule-match-btn--next" onClick={() => void handleBulkSchedule()} disabled={isBulkSaving}>
                      {isBulkSaving ? "Scheduling…" : "SCHEDULE"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <TickerAutomationModal open={tickerModalOpen} onOpenChange={setTickerModalOpen} matchId={draftMatchId} />
    </div>
  );
}
